import { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { supabase } from 'supabaseClient';
import AppContext from 'AppContext';
import _ from 'lodash';
import { createFuseInstance, applyFilters, calculateCounts } from 'utilities/profilefilterutils';

const INVALID_ENTRIES = ['n/a', 'none', 'to be determined', 'something', 'not selected', ''];

export const useProfileState = () => {
  const { platformUser } = useContext(AppContext);
  const [profiles, setProfiles] = useState(null);
  const [filteredProfiles, setFilteredProfiles] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [adminOrgs, setAdminOrgs] = useState([]);
  
  // Filter states
  const [activeFilters, setActiveFilters] = useState({
    location: null,
    sort: 'newest',
    status: null,
    organization: null,
    role: null,
    domain: null,
    profileData: null
  });

  // Filter state
  const [filterState, setFilterState] = useState({
    org: [],
    role: [],
    location: [],
    status: [],
    sort: 'newest',
    domain: [],
    profileData: []
  });

  // Create Fuse instance
  const fuse = useMemo(() => createFuseInstance(profiles), [profiles]);

  // Fetch profiles
  const fetchProfiles = useCallback(async () => {
    if (!platformUser) {
      console.error("User ID is missing");
      return;
    }

    const token = await supabase.auth.getSession().then(({ data }) => data.session?.access_token);

    if (!token) {
      console.error("Failed to fetch token");
      return;
    }

    setLoading(true);
    setError(null);
    
    const BASE_URL = process.env.REACT_APP_ENV === 'development'
      ? process.env.REACT_APP_BACKEND_URL_LOCAL
      : process.env.REACT_APP_BACKEND_URL;

    try {
      const response = await fetch(`${BASE_URL}/org-profiles-get`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data || !data.profiles) {
        throw new Error('Invalid response format from server');
      }
      
      setProfiles(data.profiles);
      // Only set selected profile if none is currently selected
      if (!selectedProfile) {
        setSelectedProfile(data.profiles.length > 0 ? data.profiles[0] : null);
      } else {
        // Update selected profile with fresh data if it still exists
        const updatedProfile = data.profiles.find(p => p.id === selectedProfile.id);
        if (updatedProfile) {
          setSelectedProfile(updatedProfile);
        }
      }
      setAdminOrgs(data.adminOrgs || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [platformUser]);

  // Apply filters using shared logic
  useEffect(() => {
    if (profiles) {
      const filtered = applyFilters(profiles, { 
        activeFilters, 
        searchQuery, 
        fuse 
      });
      setFilteredProfiles(filtered);
    }
  }, [profiles, activeFilters, searchQuery, fuse]);

  // Calculate counts using shared logic from profilefilterutils
  const counts = useMemo(() => 
    calculateCounts(profiles, filteredProfiles, activeFilters), 
    [profiles, filteredProfiles, activeFilters]
  );

  // Event handlers
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback(({ type, value }) => {
    if (type === 'domain') {
      setActiveFilters(prev => ({
        ...prev,
        domain: value
      }));
    } else if (type === 'profileData') {
      setActiveFilters(prev => ({
        ...prev,
        profileData: value
      }));
    } else {
      setActiveFilters(prev => ({
        ...prev,
        [type]: value
      }));
    }
  }, []);

  // Handle profile click
  const handleProfileClick = useCallback((profile) => {
    setSelectedProfile(profile);
  }, []);

  // Effects
  useEffect(() => {
    if (platformUser) {
      fetchProfiles();
    }
  }, [platformUser, fetchProfiles]);

  // Set selected profile
  useEffect(() => {
    if (filteredProfiles?.length > 0) {
      if (selectedProfile) {
        const stillExists = filteredProfiles.find(p => p.id === selectedProfile.id);
        if (stillExists) {
          setSelectedProfile(stillExists);
          return;
        }
      }
      setSelectedProfile(filteredProfiles[0]);
    }
  }, [filteredProfiles, selectedProfile]);

  return {
    // State
    profiles,
    filteredProfiles,
    selectedProfile,
    loading,
    error,
    adminOrgs,
    filterState,
    activeFilters,
    counts,

    // Actions
    setProfiles,
    handleSearch,
    handleFilterChange,
    handleProfileClick,
    setFilterState
  };
}; 